import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Skull, ArrowLeft, Eye, EyeOff, Loader2, FileDown, Share2, Download, Copy, Check as CheckIcon } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const buildCaption = (kit) => {
  const num = kit.case_number || "001";
  const title = kit.case_title || "A murder mystery";
  return `We solved CASE #${num} — ${title.toUpperCase()}. The victim: ${kit.victim_name || "?"}. 🕯️🔪\n\nPersonalized murder mystery kits at wasntme.party — a @nightbloodchronicles production.\n\n#WasntMe.Party #murdermystery #dinnerparty #nightbloodchronicles`;
};

const ShareBox = ({ kit, hostToken }) => {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const certUrl = `${API}/api/host/${hostToken}/kit/certificate`;
  const siteUrl = window.location.origin;
  const caption = buildCaption(kit);
  const shortCaption = `We solved CASE #${kit.case_number || "001"} — ${(kit.case_title || "A murder mystery").toUpperCase()}. Victim: ${kit.victim_name}. 🕯️🔪`;

  // Native Web Share with image file (iOS/Android — covers IG/FB/TikTok/Snap/anything installed)
  const nativeShare = async () => {
    try {
      const res = await fetch(certUrl, { credentials: "include" });
      const blob = await res.blob();
      const file = new File([blob], `case-closed-${kit.case_number || "001"}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: shortCaption, title: "Case Closed" });
        return;
      }
      // Fallback: text-only share
      if (navigator.share) {
        await navigator.share({ title: "Case Closed", text: shortCaption, url: siteUrl });
        return;
      }
      toast.message("Your browser can't share directly. Use Download and post the image yourself.");
    } catch (err) {
      // User canceled or share failed — not an error worth toasting
      if (err?.name !== "AbortError") {
        toast.error("Share didn't open. Try Download instead.");
      }
    }
  };

  const downloadImage = async () => {
    setDownloading(true);
    try {
      const res = await fetch(certUrl, { credentials: "include" });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `case-closed-${kit.case_number || "001"}.png`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Saved! Post it to your Story.");
    } catch {
      toast.error("Download failed.");
    } finally {
      setDownloading(false);
    }
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Clipboard blocked. Long-press to copy.");
    }
  };

  // Platform-specific URL intents (only platforms with real URL sharing)
  const pinterestUrl = `https://www.pinterest.com/pin/create/button/?url=${encodeURIComponent(siteUrl)}&media=${encodeURIComponent(certUrl)}&description=${encodeURIComponent(shortCaption)}`;
  const threadsUrl = `https://www.threads.net/intent/post?text=${encodeURIComponent(`${shortCaption}\n${siteUrl}`)}`;
  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shortCaption)}&url=${encodeURIComponent(siteUrl)}`;
  const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(siteUrl)}&quote=${encodeURIComponent(shortCaption)}`;

  return (
    <div className="card-noir p-5 sm:p-6 mb-4 relative overflow-hidden" data-testid="share-box">
      <div className="absolute inset-0 bg-gradient-to-br from-[#d4af37]/10 to-transparent pointer-events-none" />
      <div className="relative">
        <div className="font-hand text-[#d4af37] rotate-[-1deg] text-base mb-1">brag about it</div>
        <h2 className="font-display text-2xl sm:text-3xl mb-2">Share the Case Closed card.</h2>
        <p className="text-xs text-[#a39797] mb-4">
          A 9:16 image, perfect for Stories and Reels. Every share is a free ad for the next host.
        </p>

        {/* Primary action: Web Share API (mobile) + Download */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button onClick={nativeShare} className="btn-primary text-sm" data-testid="share-native">
            <Share2 size={16} /> Share
          </button>
          <button onClick={downloadImage} disabled={downloading} className="btn-ghost text-sm" data-testid="share-download">
            <Download size={14} /> {downloading ? "Saving..." : "Download image"}
          </button>
          <button onClick={copyCaption} className="btn-ghost text-sm" data-testid="share-copy-caption">
            {copied ? <><CheckIcon size={14} /> Copied!</> : <><Copy size={14} /> Copy caption</>}
          </button>
        </div>

        {/* Platform-specific */}
        <div className="pt-4 border-t border-[#2d2424]">
          <div className="text-[11px] tracking-widest text-[#a39797] mb-2">OR POST DIRECTLY TO</div>
          <div className="flex flex-wrap gap-2 text-sm">
            <PlatformBtn href={pinterestUrl} label="Pinterest" testid="share-pinterest">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741a.36.36 0 0 1 .083.345c-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.748-1.378l-.747 2.853c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.627 0 12-5.373 12-12S18.627 0 12 0z"/></svg>
            </PlatformBtn>
            <PlatformBtn href={threadsUrl} label="Threads" testid="share-threads">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.964-.065-1.19.408-2.285 1.33-3.082.878-.76 2.116-1.207 3.578-1.291a13.19 13.19 0 0 1 2.973.183c-.126-.746-.379-1.339-.754-1.766-.516-.586-1.313-.88-2.371-.874-.025 0-.05 0-.075 0-.845 0-1.99.232-2.721 1.32L7.642 7.724c.98-1.454 2.568-2.256 4.464-2.256.025 0 .051 0 .076.001 3.17.02 5.058 1.957 5.245 5.422.107.064.213.13.316.197a7.21 7.21 0 0 1 2.573 2.75c.76 1.722.804 4.529-1.464 6.754-1.722 1.689-3.816 2.451-6.78 2.471Zm1.343-9.611c-.288 0-.579.009-.877.028-1.783.1-2.887.922-2.825 2.093.065 1.22 1.407 1.78 2.696 1.71 1.185-.065 2.727-.531 2.986-3.635a9.927 9.927 0 0 0-1.98-.196Z"/></svg>
            </PlatformBtn>
            <PlatformBtn href={facebookUrl} label="Facebook" testid="share-facebook">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            </PlatformBtn>
            <PlatformBtn href={twitterUrl} label="X / Twitter" testid="share-twitter">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            </PlatformBtn>
          </div>
          <div className="mt-3 text-[11px] text-[#a39797]">
            For Instagram, TikTok, Snapchat: tap <strong className="text-[#f4f0ec]">Share</strong> on mobile (opens your installed apps), or <strong className="text-[#f4f0ec]">Download</strong> and post to your Story.
          </div>
        </div>
      </div>
    </div>
  );
};

const PlatformBtn = ({ href, label, children, testid }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testid}
    className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-[#2d2424] text-[#a39797] hover:text-[#d4af37] hover:border-[#d4af37] transition-colors text-xs">
    {children} {label}
  </a>
);

const VerdictCards = ({ hostToken, kit }) => {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = () => {
    api.get(`/host/${hostToken}/verdict`).then((r) => setState(r.data))
       .catch(() => setState({ players: [], awards: [] }));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [hostToken]);

  const updateAward = async (key, value) => {
    const nextAwards = { ...(state.awards.reduce((acc, a) => ({ ...acc, [a.key]: a.awardee }), {})), [key]: value };
    setState({
      ...state,
      awards: state.awards.map((a) => a.key === key ? { ...a, awardee: value, auto_assigned: false } : a),
    });
    setSaving(true);
    try {
      await api.put(`/host/${hostToken}/verdict`, { awards: nextAwards });
    } catch {
      toast.error("Couldn't save that pick.");
    } finally {
      setSaving(false);
    }
  };

  const downloadAll = async () => {
    setDownloading(true);
    try {
      const res = await fetch(`${API}/api/host/${hostToken}/verdict/download/all`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Download failed");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `verdict-cards.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Downloaded. Share away.");
    } catch (e) {
      toast.error(e.message || "Download failed.");
    } finally {
      setDownloading(false);
    }
  };

  if (!state) return <div className="mt-4 text-sm text-[#a39797]">Loading verdict…</div>;

  const assignedCount = state.awards.filter((a) => a.awardee).length;

  return (
    <div className="mt-6 p-5 border border-[#2d2424] rounded bg-[#1a1413]" data-testid="verdict-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs tracking-widest text-[#d4af37]">THE VERDICT</div>
          <div className="text-[11px] text-[#a39797] mt-0.5">
            {assignedCount} of {state.awards.length} awards assigned — auto-filled where we could detect it
          </div>
        </div>
        <button onClick={downloadAll} disabled={downloading || assignedCount === 0}
          className="btn-primary text-xs disabled:opacity-50"
          data-testid="verdict-download-all">
          {downloading ? <><Loader2 size={12} className="animate-spin" /> Zipping</> : <><Download size={12} /> Download all</>}
        </button>
      </div>
      <div className="space-y-3">
        {state.awards.map((a) => (
          <div key={a.key} className="grid grid-cols-[1fr_auto] gap-3 items-center pb-3 border-b border-[#2d2424] last:border-b-0"
            data-testid={`verdict-row-${a.key}`}>
            <div>
              <div className="flex items-center gap-2">
                <div className="text-sm text-[#f4f0ec] font-semibold">{a.label}</div>
                {a.auto_assigned && a.awardee && (
                  <span className="text-[9px] tracking-widest uppercase text-[#d4af37] border border-[#d4af37]/50 rounded px-1.5 py-[1px]">
                    auto
                  </span>
                )}
              </div>
              {a.flavor && <div className="text-[11px] text-[#a39797] mt-0.5">{a.flavor}</div>}
            </div>
            <select
              value={a.awardee || ""}
              onChange={(e) => updateAward(a.key, e.target.value)}
              className="input-noir text-xs min-w-[120px]"
              data-testid={`verdict-select-${a.key}`}>
              <option value="">— nobody —</option>
              {state.players.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
        ))}
      </div>
      {assignedCount > 0 && (
        <div className="mt-4 text-[11px] text-[#a39797]">
          Tag us at @nightbloodchronicles with <span className="text-[#d4af37]">#WasntMe.Party</span> — we repost the best ones.
        </div>
      )}
      {saving && <div className="text-[10px] text-[#a39797] mt-2">saving…</div>}
    </div>
  );
};

const SpotifyEmbed = ({ hostToken }) => {
  const [url, setUrl] = useState("");
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [moodMix, setMoodMix] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/host/${hostToken}/spotify`).then((r) => {
      if (!alive) return;
      setUrl(r.data.playlist_url || "");
      setDraft(r.data.playlist_url || "");
    }).catch(() => {});
    api.get(`/settings/mood-mix`).then((r) => {
      if (alive) setMoodMix(r.data);
    }).catch(() => {});
    return () => { alive = false; };
  }, [hostToken]);

  const embedSrc = (() => {
    const u = (url || "").match(/open\.spotify\.com\/(playlist|album|track|episode|show)\/([A-Za-z0-9]+)/);
    if (!u) return "";
    return `https://open.spotify.com/embed/${u[1]}/${u[2]}?utm_source=generator&theme=0`;
  })();

  const save = async (nextUrl) => {
    const value = (nextUrl ?? draft).trim();
    setSaving(true);
    try {
      const r = await api.post(`/host/${hostToken}/spotify`, { playlist_url: value });
      setUrl(r.data.playlist_url);
      setDraft(r.data.playlist_url);
      setEditing(false);
      toast.success(r.data.playlist_url ? "Soundtrack saved." : "Soundtrack cleared.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save that link.");
    } finally {
      setSaving(false);
    }
  };

  const pickMood = (playlist) => {
    setDraft(playlist.url);
    save(playlist.url);
  };

  const showMix = moodMix?.enabled && (moodMix.playlists || []).length > 0 && (!url || editing);

  return (
    <div className="mt-4 p-4 bg-[#1a1413] border border-[#2d2424] rounded" data-testid="spotify-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs tracking-widest text-[#d4af37]">DINNER SOUNDTRACK</div>
        {url && !editing && (
          <button onClick={() => { setDraft(url); setEditing(true); }}
            className="text-[11px] text-[#a39797] hover:text-[#f4f0ec]"
            data-testid="spotify-edit">change</button>
        )}
      </div>
      {showMix && (
        <div className="mb-3" data-testid="mood-mix">
          <div className="text-[11px] tracking-widest text-[#a39797] mb-2">PICK A MOOD · ONE TAP</div>
          <div className="flex flex-wrap gap-2">
            {moodMix.playlists.map((p) => (
              <button key={p.id}
                onClick={() => pickMood(p)}
                disabled={saving}
                className="group text-left px-3 py-2 rounded-md border border-[#2d2424] hover:border-[#d4af37] bg-[#0a0908] transition-colors disabled:opacity-50"
                data-testid={`mood-mix-${p.id}`}>
                <div className="text-sm text-[#f4f0ec] group-hover:text-[#d4af37]">{p.label}</div>
                {p.subtitle && <div className="text-[10px] text-[#a39797]">{p.subtitle}</div>}
              </button>
            ))}
          </div>
          <div className="text-[11px] text-[#a39797] mt-2">— or paste your own —</div>
        </div>
      )}
      {!url || editing ? (
        <>
          <div className="flex gap-2">
            <input type="url"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="https://open.spotify.com/playlist/..."
              className="input-noir text-sm flex-1"
              data-testid="spotify-input" />
            <button onClick={() => save()} disabled={saving}
              className="btn-primary text-sm disabled:opacity-50"
              data-testid="spotify-save">
              {saving ? <Loader2 size={14} className="animate-spin" /> : "Save"}
            </button>
          </div>
          {editing && (
            <button onClick={() => { setDraft(url); setEditing(false); }}
              className="text-[11px] text-[#a39797] hover:text-[#f4f0ec] mt-2"
              data-testid="spotify-cancel">cancel</button>
          )}
        </>
      ) : embedSrc ? (
        <iframe
          src={embedSrc}
          width="100%"
          height="152"
          frameBorder="0"
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
          loading="lazy"
          title="Spotify player"
          className="rounded"
          data-testid="spotify-embed" />
      ) : (
        <div className="text-sm text-[#a39797]">Saved — but that link isn't embeddable. Paste a Spotify Share link.</div>
      )}
    </div>
  );
};

const GhostAudio = ({ hostToken }) => {
  const [clips, setClips] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get(`/host/${hostToken}/ghost-audio`).then((r) => {
      if (alive) setClips(r.data.clips || []);
    }).catch(() => {}).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [hostToken]);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/host/${hostToken}/ghost-audio/generate`);
      setClips(r.data.clips || []);
      toast.success("Ghost audio summoned.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Ghost audio failed. Try again in a moment.");
    } finally {
      setGenerating(false);
    }
  };

  const downloadAll = async () => {
    try {
      const res = await fetch(`${API}/api/host/${hostToken}/ghost-audio/download/all`);
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ghost-audio-pack.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Saved. Load it on the speaker before the toast.");
    } catch {
      toast.error("Download failed. Try again from a browser.");
    }
  };

  return (
    <div className="mt-4 p-4 bg-[#8b0000]/10 border border-[#8b0000] rounded" data-testid="ghost-audio-panel">
      <div className="text-xs tracking-widest text-[#8b0000] mb-2">GHOST AUDIO PACK · PREMIUM</div>
      {loading ? (
        <div className="text-sm text-[#a39797]">Loading…</div>
      ) : clips.length === 0 ? (
        <>
          <p className="text-sm text-[#f4f0ec] mb-3">
            Three haunted voice clips, custom to your victim: the last voicemail, a between-rounds whisper, and the final toast. Summon them below — generation takes ~30 seconds.
          </p>
          <button onClick={generate} disabled={generating}
            className="btn-primary text-sm disabled:opacity-50"
            data-testid="ghost-audio-generate">
            {generating ? <><Loader2 size={14} className="animate-spin" /> Summoning…</> : <>🕯️ Summon the ghost</>}
          </button>
        </>
      ) : (
        <div className="space-y-3" data-testid="ghost-audio-clips">
          <div className="flex items-center justify-between pb-2 border-b border-[#8b0000]/30 mb-2">
            <div className="text-[11px] text-[#a39797]">3 clips · ready for the venue</div>
            <button onClick={downloadAll} className="btn-ghost text-xs"
              data-testid="ghost-audio-download-all">
              <Download size={12} /> Download all
            </button>
          </div>
          {clips.map((c) => (
            <div key={c.id} className="pb-3 border-b border-[#8b0000]/30 last:border-b-0" data-testid={`ghost-clip-${c.id}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="font-display text-base text-[#f4f0ec]">{c.title}</div>
                <div className="text-[10px] tracking-widest text-[#d4af37]">{c.voice_label}</div>
              </div>
              <audio controls preload="none"
                src={`${API}/api/host/${hostToken}/ghost-audio/${c.id}`}
                className="w-full mt-1" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


const Section = ({ title, kicker, children, testid }) => (
  <section className="card-noir p-5 sm:p-6 mb-4" data-testid={testid}>
    <div className="font-hand text-[#d4af37] rotate-[-1deg] text-base mb-1">{kicker}</div>
    <h2 className="font-display text-2xl sm:text-3xl mb-4">{title}</h2>
    {children}
  </section>
);

const RoleCard = ({ card, idx }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#2d2424] rounded-lg mb-3" data-testid={`role-card-${idx}`}>
      <button onClick={() => setOpen((o) => !o)}
        className="w-full text-left p-4 flex items-center justify-between hover:bg-[#2d2424]/30 transition-colors"
        data-testid={`role-toggle-${idx}`}>
        <div>
          <div className="font-display text-lg text-[#f4f0ec]">{card.archetype}</div>
          <div className="text-sm text-[#a39797]">Played by <span className="text-[#d4af37]">{card.player_name}</span></div>
        </div>
        <div className="text-[#a39797] flex items-center gap-2 text-xs">
          {open ? <><EyeOff size={14} /> Hide</> : <><Eye size={14} /> Reveal card</>}
        </div>
      </button>
      {open && (
        <div className="px-4 pb-5 border-t border-[#2d2424] pt-4 space-y-3 text-sm text-[#f4f0ec]">
          <p>{card.blurb}</p>
          {card.voice && <p><span className="text-[#d4af37] text-xs tracking-widest">VOICE:</span> {card.voice}</p>}
          {card.energy_tag && <p><span className="text-[#d4af37] text-xs tracking-widest">YOUR ENERGY:</span> {card.energy_tag}</p>}
          {card.signature_line && <p><span className="text-[#d4af37] text-xs tracking-widest">SIGNATURE LINE:</span> "{card.signature_line}"</p>}
          {card.accused_secret && <p><span className="text-[#d4af37] text-xs tracking-widest">ACCUSED OF:</span> {card.accused_secret}</p>}
          {card.is_betrayer && card.betrayer_twist && (
            <div className="mt-3 p-3 bg-[#8b0000]/15 border border-[#8b0000] rounded">
              <div className="text-xs tracking-widest text-[#8b0000] mb-1">BETRAYAL CHOICE — EYES ONLY</div>
              <p className="text-sm">{card.betrayer_twist}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ScreenKit = () => {
  const { hostToken } = useParams();
  const [kit, setKit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revealOpen, setRevealOpen] = useState(false);

  useEffect(() => {
    api.get(`/host/${hostToken}/kit/preview`).then((r) => {
      setKit(r.data);
    }).catch(() => {
      toast.error("Kit not found or not yet locked.");
    }).finally(() => setLoading(false));
  }, [hostToken]);

  if (loading) {
    return (
      <div className="grain min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-[#8b0000]" size={32} />
      </div>
    );
  }

  if (!kit) {
    return (
      <div className="grain min-h-screen flex items-center justify-center px-6 text-center">
        <div>
          <div className="font-display text-3xl mb-3 text-[#f4f0ec]">Kit not found.</div>
          <p className="text-[#a39797] mb-6">The kit may not be locked yet, or this link is invalid.</p>
          <Link to="/" className="btn-primary"><ArrowLeft size={16} /> Back</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="grain min-h-screen pb-24" data-testid="screen-kit-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <Link to={`/dashboard/${hostToken}`} className="inline-flex items-center gap-2 text-sm text-[#a39797] hover:text-[#d4af37] mb-6" data-testid="back-to-dashboard">
          <ArrowLeft size={14} /> back to dashboard
        </Link>

        <div className="mb-6">
          <div className="font-hand text-[#d4af37] rotate-[-2deg] text-2xl mb-2">play on screen</div>
          <h1 className="font-display text-4xl text-[#f4f0ec]">
            <Skull size={28} className="inline text-[#8b0000] mr-2" />
            {kit.case_title || "Your mystery"}
          </h1>
          <p className="text-sm text-[#a39797] mt-2">
            No printer, no props — pass your phone or tablet around. The reveal stays locked until you tap it.
          </p>
        </div>

        <Section title="Host Script" kicker="read aloud" testid="section-host-script">
          <div className="text-sm text-[#f4f0ec] whitespace-pre-wrap leading-relaxed">
            {kit.host_script}
          </div>
        </Section>

        <Section title="Clue Set A" kicker="round 2" testid="section-clue-a">
          <ol className="space-y-2 text-sm text-[#f4f0ec] list-decimal pl-5">
            {kit.clue_set_a?.map((c, i) => <li key={i} className="leading-relaxed">{c}</li>)}
          </ol>
        </Section>

        <Section title="Clue Set B" kicker="round 3" testid="section-clue-b">
          <ol className="space-y-2 text-sm text-[#f4f0ec] list-decimal pl-5">
            {kit.clue_set_b?.map((c, i) => <li key={i} className="leading-relaxed">{c}</li>)}
          </ol>
        </Section>

        {kit.wedding_mode && kit.love_story_vault?.length > 0 && (
          <Section title="The Love Story Vault" kicker="read between rounds" testid="section-love-vault">
            <div className="space-y-4 text-sm text-[#f4f0ec]">
              {kit.love_story_vault.map((e, i) => (
                <div key={i} className="pb-3 border-b border-[#2d2424] last:border-b-0">
                  <div className="font-display text-lg">{e.player_name} <span className="text-[#a39797] text-sm font-sans">— {e.archetype}</span></div>
                  {e.memory && <p className="mt-1"><span className="text-[#d4af37] text-xs tracking-widest">MEMORY:</span> {e.memory}</p>}
                  {e.wish && <p><span className="text-[#d4af37] text-xs tracking-widest">WISH:</span> {e.wish}</p>}
                </div>
              ))}
              {kit.ghost_audio_pack && <GhostAudio hostToken={hostToken} />}
              <SpotifyEmbed hostToken={hostToken} />
            </div>
          </Section>
        )}

        <Section title="Role Cards" kicker="tap to reveal — one at a time" testid="section-roles">
          <div className="text-xs text-[#a39797] mb-3">Each card is a spoiler. Reveal one card per player, then pass the device.</div>
          {kit.role_cards?.map((rc, i) => <RoleCard key={i} card={rc} idx={i} />)}
        </Section>

        <Section title="The Reveal" kicker="only after the final vote" testid="section-reveal">
          {!revealOpen ? (
            <div className="text-center py-6">
              <p className="text-[#a39797] mb-4 text-sm">
                Everyone has written down their suspect? Votes are in?
              </p>
              <button onClick={() => setRevealOpen(true)} className="btn-primary"
                data-testid="reveal-unlock">
                <Eye size={16} /> Unlock the reveal
              </button>
            </div>
          ) : (
            <div className="text-sm text-[#f4f0ec] whitespace-pre-wrap leading-relaxed" data-testid="reveal-text">
              {kit.reveal}
            </div>
          )}
        </Section>

        {/* Share module — always available, extra-prominent after reveal */}
        <ShareBox kit={kit} hostToken={hostToken} />
        <div className="text-center mb-6">
          <Link to={`/recap/${hostToken}`} className="btn-primary"
            data-testid="screenkit-open-recap">
            Open the recap page → survey · shares · referral
          </Link>
        </div>
        <VerdictCards kit={kit} hostToken={hostToken} />

        <div className="mt-6 text-center">
          <a href={`${API}/api/host/${hostToken}/kit/pdf`}
            target="_blank" rel="noopener noreferrer"
            className="btn-ghost text-sm" data-testid="also-print">
            <FileDown size={14} /> Also want to print? US Letter PDF
          </a>
        </div>
      </div>
    </div>
  );
};

export default ScreenKit;
